import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui';
import { useStore } from '../store';
import {
  getAnalysisHistory,
  submitAnalysisBase64,
} from '../services/api';
import type { AnalysisHistoryItem } from '../types';
import { trackEvent } from '../analytics';

type HistoryStatus = 'loading' | 'polling' | 'ready' | 'error';

const MAX_POLL_ATTEMPTS = 36; // 3 minutes @ 5s

export function ResultsHistoryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, onboarding, setAnalysis } = useStore();
  const [items, setItems] = useState<AnalysisHistoryItem[]>([]);
  const [status, setStatus] = useState<HistoryStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const attemptsRef = useRef(0);
  const hasSubmittedRef = useRef(false);
  const hasTrackedPaymentRef = useRef(false);

  const getLatestStatus = (list: AnalysisHistoryItem[]) => {
    const latest = list[0];
    return latest?.status?.toUpperCase?.() ?? null;
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const scheduleNext = () => {
    stopPolling();
    pollRef.current = window.setTimeout(() => {
      void poll();
    }, 5000);
  };

  const poll = async () => {
    if (!user.userId) return;

    attemptsRef.current += 1;
    setStatus((prev) => (prev === 'ready' ? prev : 'polling'));

    try {
      const submissionKey = `umax-analysis-submitted-${user.userId}`;
      const alreadySubmitted = localStorage.getItem(submissionKey);
      const frontBase64 = localStorage.getItem('umax-front-base64');
      const sideBase64 = localStorage.getItem('umax-side-base64');
      const storedGender = onboarding.gender;
      const ratingType = onboarding.ratingType;

      if (
        frontBase64 &&
        sideBase64 &&
        storedGender &&
        ratingType &&
        !alreadySubmitted &&
        !hasSubmittedRef.current
      ) {
        hasSubmittedRef.current = true;
        localStorage.setItem(submissionKey, '1');
        await submitAnalysisBase64(
          user.userId,
          storedGender,
          ratingType,
          frontBase64,
          sideBase64
        );
      }

      const history = await getAnalysisHistory(user.userId);
      const list = history.items || [];
      setItems(list);

      const latestStatus = getLatestStatus(list);
      if (!latestStatus || latestStatus === 'IN_PROGRESS' || latestStatus === 'PROCESSING') {
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus('error');
          setError('Analysis is taking longer than expected. Please try again later.');
          stopPolling();
          return;
        }
        scheduleNext();
        return;
      }

      if (latestStatus === 'ERROR' || latestStatus === 'FAILED') {
        setStatus('error');
        setError('We could not complete your analysis. Please try again.');
        stopPolling();
        return;
      }

      setStatus('ready');
      stopPolling();
    } catch {
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setStatus('error');
        setError('Unable to load results. Please try again.');
        stopPolling();
        return;
      }
      scheduleNext();
    }
  };

  useEffect(() => {
    if (!user.userId) return;
    attemptsRef.current = 0;
    setStatus('loading');
    setError(null);
    void poll();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userId, searchParams.toString()]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !hasTrackedPaymentRef.current) {
      hasTrackedPaymentRef.current = true;
      trackEvent('payment_success', { sessionId });
    }
  }, [searchParams]);

  const handleOpenResult = (item: AnalysisHistoryItem) => {
    setAnalysis(item);
    navigate('/results/view', { state: { result: item } });
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="px-6 py-6 max-w-md mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button className="text-secondary text-sm" onClick={() => navigate('/')}>
            Close
          </button>
          <h1 className="text-base font-semibold text-white">Your Results</h1>
          <div className="w-10" />
        </div>

        {status === 'polling' && (
          <div className="mb-6 rounded-2xl bg-surface border border-white/10 px-4 py-3">
            <p className="text-sm text-secondary">Analyzing your face...</p>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-2xl bg-red-500/10 border border-red-500/30 px-4 py-3">
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {items.length === 0 ? (
          <div className="rounded-2xl bg-surface border border-white/10 px-4 py-6 text-center">
            <p className="text-secondary text-sm">No results yet. Please wait...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              const statusLabel = item.status?.toUpperCase?.() || 'UNKNOWN';
              const isReady = statusLabel === 'COMPLETED';
              const badgeClass =
                statusLabel === 'COMPLETED'
                  ? 'status-badge status-badge--success'
                  : statusLabel === 'IN_PROGRESS' || statusLabel === 'PROCESSING'
                  ? 'status-badge status-badge--warning'
                  : statusLabel === 'FAILED' || statusLabel === 'ERROR'
                  ? 'status-badge status-badge--error'
                  : 'status-badge status-badge--neutral';
              const thumb = item.imgUrls?.[0];
              return (
                <button
                  key={`${statusLabel}-${index}`}
                  onClick={() => isReady && handleOpenResult(item)}
                  className="w-full rounded-2xl bg-surface border border-white/10 px-4 py-4 flex items-center gap-4 text-left"
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-black/40 border border-white/10 flex-shrink-0">
                    {thumb ? (
                      <img src={thumb} alt="Result" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">Analysis #{items.length - index}</p>
                    <p className="text-xs text-secondary mt-1">Status: {statusLabel}</p>
                  </div>
                  <span className={badgeClass}>{statusLabel}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-8">
          <Button onClick={() => void poll()}>Refresh</Button>
        </div>
      </div>
    </div>
  );
}
